import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { UserRoles, UserStatus } from '../core/state/users/models';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthGuardService {
  async canActivate(
    authService: AuthService,
    currentRoute: string,
  ): Promise<boolean | UrlTree> {
    return authService.isAuthenticated().then((isAuth) => {
      if (!isAuth) {
        authService.redirectToLogin(currentRoute);
      }
      return isAuth;
    });
  }
}

export const canActivateProtected: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authGuardService = inject(AuthGuardService);
  const authService = inject(AuthService);
  const authStatusGuardService = inject(AuthStatusGuardService);

  const isAuth = await authGuardService.canActivate(authService, state.url);
  if (!isAuth) return false;

  return authStatusGuardService.canActivate(authService);
};

@Injectable()
export class AuthorizationGuardService {
  async canActivate(
    authService: AuthService,
    route: ActivatedRouteSnapshot,
  ): Promise<boolean | UrlTree> {
    const allowedRoles = (route.data['canAccessList'] as string[]) || [
      UserRoles.ADMIN,
    ];

    return authService.hasRequiredRole(allowedRoles).then((hasRole) => {
      if (!hasRole) {
        authService.redirectToHome();
      }
      return hasRole;
    });
  }
}

export const canActivateRoles: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(AuthorizationGuardService).canActivate(
    inject(AuthService),
    route,
  );
};

@Injectable()
export class AuthStatusGuardService {
  async canActivate(authService: AuthService): Promise<boolean | UrlTree> {
    return authService.getDecodedToken().then((token) => {
      if (!token) {
        authService.redirectToLogin('');
        return false;
      }
      if (token.user_status === UserStatus.VERIFIED) {
        return true;
      }
      if (token.user_status === UserStatus.PENDING_PARENTAL_CONSENT) {
        authService.redirectToPendingConsent();
      } else {
        authService.redirectToVerifyUser();
      }
      return false;
    });
  }
}

export const canActivateStatus: CanActivateFn = () => {
  return inject(AuthStatusGuardService).canActivate(inject(AuthService));
};

export const canActivateGuest: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const isAuth = await authService.isAuthenticated();
  if (!isAuth) {
    return true;
  }

  const token = await authService.getDecodedToken();
  if (token?.user_status === UserStatus.PENDING_PARENTAL_CONSENT) {
    return router.createUrlTree(['/auth/pending-consent']);
  }
  if (token?.user_status === UserStatus.UNVERIFIED) {
    return router.createUrlTree(['/auth/verify-user']);
  }
  if (token?.user_role === UserRoles.ADMIN) {
    return router.createUrlTree(['/admin']);
  }
  return router.createUrlTree(['/dashboard/overview']);
};

